import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Button, Form, FormInstance, InputNumber, Radio } from "antd";
import { TMSPalletDimension } from "@/types";
import Compact from "antd/es/space/Compact";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";

type DimUnit = "m" | "cm" | "in" | "ft";
type WeightUnit = "kg" | "lb";

const dimUnitOptions = [
  { label: "m", value: "m" },
  { label: "cm", value: "cm" },
  { label: "in", value: "in" },
  { label: "ft", value: "ft" },
] as const;

const weightUnitOptions = [
  { label: "kg", value: "kg" },
  { label: "lb", value: "lb" },
] as const;

const initialValues: TMSPalletDimension = {
  width: 0,
  height: 0,
  length: 0,
  weight: 0,
};

function toMeters(value: number, unit: DimUnit): number {
  switch (unit) {
    case "m":
      return value;
    case "cm":
      return value / 100;
    case "in":
      return value * 0.0254;
    case "ft":
      return value * 0.3048;
  }
}

function fromMeters(valueM: number, unit: DimUnit): number {
  switch (unit) {
    case "m":
      return valueM;
    case "cm":
      return valueM * 100;
    case "in":
      return valueM / 0.0254;
    case "ft":
      return valueM / 0.3048;
  }
}

function toKg(value: number, unit: WeightUnit): number {
  return unit === "kg" ? value : value * 0.45359237;
}

function fromKg(valueKg: number, unit: WeightUnit): number {
  return unit === "kg" ? valueKg : valueKg / 0.45359237;
}

function round(n: number, digits = 4) {
  const p = 10 ** digits;
  return Math.round(n * p) / p;
}

interface PalletDimensionProps {
  initialDimUnit?: DimUnit;
  initialWeightUnit?: WeightUnit;
}

export interface PalletDimensionFormRef {
  getFormRef: () => FormInstance<TMSPalletDimension>;
  getValues: () => Promise<TMSPalletDimension>;
}

const PalletDimensionForm = forwardRef<
  PalletDimensionFormRef,
  PalletDimensionProps
>((props, ref) => {
  const { initialDimUnit = "in", initialWeightUnit = "lb" } = props;
  const [form] = Form.useForm<TMSPalletDimension>();

  const [dimUnit, setDimUnit] = useState<DimUnit>(initialDimUnit);
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(initialWeightUnit);

  const requiredPositiveRule = useMemo(
    () => [
      { required: true, message: "Required" },
      {
        validator: async (_: any, v: any) => {
          if (v == null || v === "") return;
          if (typeof v !== "number" || Number.isNaN(v))
            throw new Error("Must be a number");
          if (v <= 0) throw new Error("Must be greater than 0");
        },
      },
    ],
    [],
  );

  const convertExistingDimensions = useCallback(
    (nextUnit: DimUnit) => {
      const { width, height, length } = form.getFieldsValue();
      const next: Partial<TMSPalletDimension> = {};

      if (typeof width === "number")
        next.width = round(fromMeters(toMeters(width, dimUnit), nextUnit), 4);
      if (typeof height === "number")
        next.height = round(fromMeters(toMeters(height, dimUnit), nextUnit), 4);
      if (typeof length === "number")
        next.length = round(fromMeters(toMeters(length, dimUnit), nextUnit), 4);

      form.setFieldsValue(next);
      setDimUnit(nextUnit);
    },
    [form, dimUnit],
  );

  const convertExistingWeight = useCallback(
    (nextUnit: WeightUnit) => {
      const { weight } = form.getFieldsValue();
      if (typeof weight === "number") {
        const asKg = toKg(weight, weightUnit);
        form.setFieldsValue({ weight: round(fromKg(asKg, nextUnit), 4) });
      }
      setWeightUnit(nextUnit);
    },
    [form, weightUnit],
  );

  useImperativeHandle(
    ref,
    () => ({
      getFormRef: () => form,
      getValues: async () => {
        return form.validateFields().then((values) => {
          return {
            width: round(toMeters(values.width, dimUnit), 3),
            height: round(toMeters(values.height, dimUnit), 3),
            length: round(toMeters(values.length, dimUnit), 3),
            weight: round(toKg(values.weight, weightUnit), 3),
          };
        });
      },
    }),
    [form, dimUnit, weightUnit],
  );

  return (
    <Form<TMSPalletDimension>
      form={form}
      layout="horizontal"
      initialValues={initialValues}
      labelCol={{ style: { width: 120 } }}
      labelAlign="left"
    >
      <div className="mt-1 mb-3 flex flex-row justify-between items-center ml-2">
        <div className="w-[113px]">Units</div>
        <div>
          <Radio.Group
            size="small"
            value={dimUnit}
            onChange={(e) =>
              convertExistingDimensions(e.target.value as DimUnit)
            }
          >
            {dimUnitOptions.map((option) => (
              <Radio.Button key={option.value} value={option.value}>
                {option.label}
              </Radio.Button>
            ))}
          </Radio.Group>
          <Radio.Group
            className="!ml-4"
            size="small"
            value={weightUnit}
            onChange={(e) =>
              convertExistingWeight(e.target.value as WeightUnit)
            }
          >
            {weightUnitOptions.map((option) => (
              <Radio.Button key={option.value} value={option.value}>
                {option.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      </div>
      <Form.Item label={`Width (${dimUnit})`} required>
        <div className="flex flex-row justify-end w-full">
          <Compact>
            <Button
              icon={<MinusOutlined />}
              onClick={() => {
                const w = form.getFieldValue("width") ?? 0;
                form.setFieldsValue({ width: Math.max(0, w - 0.1) });
              }}
            />

            <Form.Item name="width" rules={requiredPositiveRule} noStyle>
              <InputNumber min={0} controls={false} step={0.01} precision={2} />
            </Form.Item>

            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                const w = form.getFieldValue("width") ?? 0;
                form.setFieldsValue({ width: w + 0.1 });
              }}
            />
          </Compact>
        </div>
      </Form.Item>

      <Form.Item label={`Height (${dimUnit})`} required>
        <div className="flex flex-row justify-end w-full">
          <Compact>
            <Button
              icon={<MinusOutlined />}
              onClick={() => {
                const h = form.getFieldValue("height") ?? 0;
                form.setFieldsValue({ height: Math.max(0, h - 0.1) });
              }}
            />

            <Form.Item name="height" rules={requiredPositiveRule} noStyle>
              <InputNumber min={0} controls={false} step={0.01} precision={2} />
            </Form.Item>

            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                const h = form.getFieldValue("height") ?? 0;
                form.setFieldsValue({ height: h + 0.1 });
              }}
            />
          </Compact>
        </div>
      </Form.Item>

      <Form.Item label={`Length (${dimUnit})`} required>
        <div className="flex flex-row justify-end w-full">
          <Compact>
            <Button
              icon={<MinusOutlined />}
              onClick={() => {
                const l = form.getFieldValue("length") ?? 0;
                form.setFieldsValue({ length: Math.max(0, l - 0.1) });
              }}
            />

            <Form.Item name="length" rules={requiredPositiveRule} noStyle>
              <InputNumber min={0} controls={false} step={0.01} precision={2} />
            </Form.Item>

            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                const l = form.getFieldValue("length") ?? 0;
                form.setFieldsValue({ length: l + 0.1 });
              }}
            />
          </Compact>
        </div>
      </Form.Item>

      <Form.Item label={`Weight (${weightUnit})`} required>
        <div className="flex flex-row justify-end w-full">
          <Compact>
            <Button
              icon={<MinusOutlined />}
              onClick={() => {
                const w = form.getFieldValue("weight") ?? 0;
                form.setFieldsValue({ weight: Math.max(0, w - 0.1) });
              }}
            />

            <Form.Item name="weight" rules={requiredPositiveRule} noStyle>
              <InputNumber min={0} controls={false} step={0.01} precision={2} />
            </Form.Item>

            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                const w = form.getFieldValue("weight") ?? 0;
                form.setFieldsValue({ weight: w + 0.1 });
              }}
            />
          </Compact>
        </div>
      </Form.Item>
    </Form>
  );
});

PalletDimensionForm.displayName = "PalletDimensionForm";

export default PalletDimensionForm;
